import { get, post, put } from './http';
import { PaginatedResponse } from './types';

// Types sourced from https://github.com/ev-proddev/user-affiliations
// Paths below are relative to that repo root (user-affiliations-dto/src/main/java/com/experticity/affiliations/dto/).

// ── Enums ─────────────────────────────────────────────────────────────────────

/** @source enums/DisplayableRelationshipStatus.java */
export enum AffiliationStatus {
  NO_STATUS = 'NO_STATUS',
  ACTIVE = 'ACTIVE',
  DENIED = 'DENIED',
  EXPIRED = 'EXPIRED',
  EXPIRING_SOON = 'EXPIRING_SOON',
  PENDING = 'PENDING',
}

/** @source ev-proddev/experticity-constants — ApplicationStatus.java */
export enum ApplicationStatus {
  APPLICANT_RESPONDED = 'APPLICANT_RESPONDED',
  APPROVED = 'APPROVED',
  AWAITING_MANUAL_APPROVAL = 'AWAITING_MANUAL_APPROVAL',
  DENIED = 'DENIED',
  DRAFT = 'DRAFT',
  EXPIRED = 'EXPIRED',
  IN_PROCESS = 'IN_PROCESS',
  NEW = 'NEW',
  PRE_APPROVED = 'PRE_APPROVED',
  PRE_DENIED = 'PRE_DENIED',
}

/** @source ev-proddev/experticity-constants — ApplicationType.java */
export enum ApplicationType {
  NEW = 'NEW',
  RENEWAL = 'RENEWAL',
}

/** @source ev-proddev/experticity-constants — QuestionAttachmentPreference.java */
export enum AttachmentPreferences {
  NONE = 'NONE',
  OPTIONAL = 'OPTIONAL',
  REQUIRED = 'REQUIRED',
}

/**
 * Error type codes originating from the AutoCred service (autocred-xapi ErrorType class).
 * Passed through as a raw string — not a Java enum in user-affiliations.
 * @source DocumentVerificationErrorDTO.java (errorType field)
 */
export enum DocumentVerificationErrorType {
  NAME_MISMATCH = 'NAME_MISMATCH',
  DOCUMENT_TYPE_INVALID = 'DOCUMENT_TYPE_INVALID',
  MISSING_FIELD = 'MISSING_FIELD',
  VALIDATION_FAILED = 'VALIDATION_FAILED',
}

/** @source enums/DocumentVerificationStatus.java */
export enum DocumentVerificationStatus {
  QUEUED = 'QUEUED',
  REQUEUED = 'REQUEUED',
  PROCESSING = 'PROCESSING',
  PROCESSING_FAILED = 'PROCESSING_FAILED',
  FRAUD_SUSPECTED = 'FRAUD_SUSPECTED',
  VALID = 'VALID',
  INVALID = 'INVALID',
  AFFILIATION_CREATED = 'AFFILIATION_CREATED',
  PAUSED = 'PAUSED',
}

/** @source enums/EmailFailureCode.java */
export enum EmailFailureCode {
  EMAIL_UNUSABLE = 'EMAIL_UNUSABLE',
  EMAIL_PATTERN_MATCH_FAILED = 'EMAIL_PATTERN_MATCH_FAILED',
}

/** @source ev-proddev/experticity-constants — QuestionType.java */
export enum QuestionType {
  COMMENT = 'COMMENT',
  CONFIRMATION = 'CONFIRMATION', // transient — frontend-only state, not in Java source
  CONNECTION = 'CONNECTION',
  DATE = 'DATE',
  IMAGE = 'IMAGE',
  LOCATION = 'LOCATION', // transient — frontend-only state, not in Java source
  MCMA = 'MCMA',
  MCSA = 'MCSA',
  NUMERIC = 'NUMERIC',
  RATING = 'RATING',
}

/** @source enums/ApplicationRuleFailureStatus.java */
export enum RuleFailureMode {
  DECLINE = 'DECLINE',
  MANUAL_REVIEW = 'MANUAL_REVIEW',
}

/** @source enums/TeamCodeStatus.java */
export enum TeamCodeStatus {
  INVALID = 'INVALID',
  NOT_ACTIVE_YET = 'NOT_ACTIVE_YET',
  SINGLE_USE_ALREADY_USED = 'SINGLE_USE_ALREADY_USED',
  NO_LONGER_VALID = 'NO_LONGER_VALID',
  VALID = 'VALID',
  ARCHIVED = 'ARCHIVED',
}

/** @source ev-proddev/experticity-constants — TeamType.java */
export enum TeamType {
  MANUFACTURER = 'MANUFACTURER',
  PARTNER = 'PARTNER',
  PRO = 'PRO',
  PROGRAM = 'PROGRAM',
  PROMOTIVE = 'PROMOTIVE',
  RETAIL = 'RETAIL',
}

// ── Types ─────────────────────────────────────────────────────────────────────

/** @source ShippingAddressDto.java */
export type ShippingAddress = {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  countryCode?: string;
};

/** @source LocationDto.java */
export type Location = {
  locationId: number;
  orgId?: number;
  description?: string;
  locationNumber?: string;
  contactName?: string;
  phone?: string;
  email?: string;
  createdTime?: string;
  modifiedTime?: string;
  address?: ShippingAddress;
  shippingAddress?: ShippingAddress;
};

/** @source MediaFileDTO.java */
export type MediaFile = {
  imageName?: string | null;
  imagePath?: string | null;
  width?: number | null;
  height?: number | null;
  defaultImagePath?: string;
};

/** @source UserStatusDto.java — note: Java `isExpired` serializes as `expired` over JSON */
export type UserStatus = {
  status: AffiliationStatus;
  label?: string;
  expirationDate?: string;
  expired: boolean;
  expiringSoon: boolean;
};

/** @source AttachmentDto.java */
export type Attachment = {
  secureFileUid?: string;
  description?: string;
  imageName?: string;
  imagePath?: string;
  contributorUserId?: number;
  createdTime?: string;
  modifiedTime?: string;
};

/** @source AnswerDTO.java */
export type Answer = {
  answerId: number;
  text: string;
  sortOrder: number;
};

/** @source QuestionDTO.java */
export type Question = {
  questionId: number;
  text: string;
  questionType: QuestionType;
  requireAnswer?: boolean;
  requireAnswerText?: string;
  sortOrder: number;
  attachmentPreference: AttachmentPreferences;
  field?: string;
  comment?: string;
  userAnswers?: number[];
  attachment?: Attachment;
  secureFileUid?: string;
  answers?: Answer[];
  helpLabel?: string;
  helpText?: string;
  teamInfluencerClassQuestion?: boolean;
};

/** @source ApplicationFormDTO.java */
export type ApplicationForm = {
  teamApplicationFormId: number;
  name?: string;
  userUuid: string;
  teamId: number;
  teamType?: TeamType;
  teamCode?: string;
  locationId?: number;
  applicationType: ApplicationType;
  verificationEmail?: string;
  emailRequired: boolean;
  emailRuleFailureMode?: RuleFailureMode;
  questions: Question[];
  referredOrgId?: number;
  referer?: string;
  autoApproved: boolean;
  allowQuestionSkipWithEmail: boolean;
  useAuthMemberList: boolean;
};

/** @source TeamDTO.java */
export type Team = {
  teamId: number;
  name: string;
  shortName?: string;
  description?: string;
  type: TeamType;
  orgId?: number;
  orgKey?: string;
  teamKey?: string;
  memberCount?: number;
  brandCount?: number;
  locationCount?: number;
  location?: Location;
  teamImage?: MediaFile;
  teamImageOverride?: MediaFile;
  userStatus?: UserStatus;
  publicApplicationFormId?: number;
  renewalApplicationFormId?: number;
  applicationsEnabled: boolean;
  applicationsExpire?: boolean;
  privateToTeamSearch?: boolean;
  privateToProfile?: boolean;
  requireMemberNumber?: boolean;
  validateMemberNumber?: boolean;
  teamClassIds?: number[];
  useDocumentVerification?: boolean;
  /** @sync-ignore — partial DTO; org status only typed here, sourced from TeamOrganizationDTO.java via the myTeams team payload */
  teamOrganizationDTO?: { orgStatus?: number } & Record<string, unknown>;
};

/** @source TeamCodeDTO.java */
export type TeamCode = {
  teamCodeId?: number;
  codeType?: string;
  code: string;
  maxRedemptions?: number;
  remainingRedemptionCount?: number;
  startDate?: string;
  endDate?: string;
  expirationType?: string;
  expirationDays?: number;
  expirationDate?: string;
  displayCode?: string;
};

/** @source AffiliationDTO.java */
export type Affiliation = {
  teamRelationshipId?: number;
  affiliationStatus: AffiliationStatus;
  affiliationStatusLabel?: string;
  status?: string;
  historicalStartDate?: string;
  creationDate?: string;
  expirationDate?: string;
  memberNumber?: string;
  type?: TeamType;
  expired: boolean;
  expiringSoon: boolean;
  location?: Location;
  attachmentDtos?: Attachment[];
};

/** @source DocumentVerificationErrorDTO.java */
export type DocumentVerificationError = {
  id: number;
  errorMessage: string;
  errorType?: DocumentVerificationErrorType;
};

/**
 * @source DocumentVerificationResultDTO.java
 * @sync-ignore teamApplicationId — @JsonView(Internal); not sent in External API responses and not consumed by the widget
 * @sync-ignore fraudPct — @JsonView(Internal); same
 * @sync-ignore fraudReasoning — @JsonView(Internal); same
 * @sync-ignore processingError — @JsonView(Internal); same
 */
export type DocumentVerificationResult = {
  id: number;
  /** @sync-ignore — @JsonView(Internal); not sent in External API responses */
  teamApplicationId: number;
  attachmentId: number;
  questionId?: number;
  fileName?: string;
  status: DocumentVerificationStatus;
  errors: DocumentVerificationError[];
  startProcessingTime?: string;
  endProcessingTime?: string;
  createdTime?: string;
  /** @sync-ignore — @JsonView(Internal); not sent in External API responses */
  fraudPct?: number;
  /** @sync-ignore — @JsonView(Internal); not sent in External API responses */
  fraudReasoning?: string;
  /** @sync-ignore — @JsonView(Internal); not sent in External API responses */
  processingError?: string;
};

/** @sync-ignore — frontend utility alias, not a backend DTO */
export type DocumentVerificationResults = Record<number, DocumentVerificationResult>;

/** @source TeamApplicationDTO.java */
export type TeamApplication = {
  teamApplicationId: number;
  team: Team;
  applicationForm?: ApplicationForm;
  teamCode?: TeamCode;
  applicationStatus: ApplicationStatus;
  location?: Location;
  memberNumber?: string;
  createdTime?: string;
  modifiedTime?: string;
  expirationTime?: string;
  processedTime?: string;
  deniedMessage?: string;
  emailVerifiable?: boolean;
  emailVerificationStatus?: string;
  applicationType?: ApplicationType;
  verificationEmail?: string;
  attachments?: Attachment[];
  affiliation?: Affiliation;
  documentVerificationResults?: DocumentVerificationResult[];
  errorStatus?: string;
  errorStatusMessage?: string;
};

/** @source ApplicationProcessingSummaryDTO.java */
export type ApplicationProcessingSummary = {
  teamApplication?: TeamApplication;
  applicationStatus?: ApplicationStatus;
  affiliation?: Affiliation;
  errorStatus?: string;
  errorStatusMessage?: string;
  documentVerificationResults?: Record<number, DocumentVerificationResult>;
};

// ── Response shapes ───────────────────────────────────────────────────────────

/** @sync-ignore — frontend composition: TeamCode extended with applicationForm and team from the teamCode lookup response */
export type TeamCodeResponse = TeamCode & {
  applicationForm?: ApplicationForm;
  team: Team;
};

/** @sync-ignore — subset of TeamCodeSubmitResponse fields surfaced by the frontend; no direct DTO equivalent */
export type TeamCodeSubmitSummary = {
  applicationStatus: ApplicationStatus;
  affiliation?: Affiliation;
  errorStatus: string;
};

/** @sync-ignore — frontend composition: TeamCodeResponse extended with summary and teamCodeStatus */
export type TeamCodeSubmitResponse = TeamCodeResponse & {
  summary: TeamCodeSubmitSummary;
  teamCodeStatus: TeamCodeStatus;
};

/** @source EmailRuleDTO.java */
export type EmailRule = {
  requestedEmail?: string;
  requestedEmailPassed: boolean;
  emailFailureCode?: EmailFailureCode;
  emailRules?: string[];
  emailRuleFailureMode: RuleFailureMode;
};

/** @sync-ignore — frontend response wrapper for the onboarding affiliations endpoint */
export type OnboardingAffiliationsResponse = {
  potentialAffiliations: Array<{ teamId: number }>;
};

/** @sync-ignore — frontend composition: team, affiliation, and latestApplication joined from the myTeams response */
export type MyTeamEntry = {
  affiliation?: Affiliation;
  latestApplication?: TeamApplication;
  team: Team;
};

// ── Request shapes ────────────────────────────────────────────────────────────

/** @sync-ignore — request body shape; backend class path unknown, find via /sync-api-types once auth is set up */
export type CreateLocationRequest = {
  orgId: number;
  teamId?: number;
  teamKey?: string;
  createdBy?: number;
  addressId?: number;
  locationNumber?: string;
  exportKey?: string;
  description?: string;
  contactName?: string;
  numberOfEmployees?: number;
  phone?: string;
  fax?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  subdivisionId?: number;
  postalCode?: string;
  countryCode?: string;
  countryCodeId?: number;
};

/** @sync-ignore — frontend options bag for getLocations query params */
export type FetchLocationsOptions = {
  teamId: number;
  latitude?: number;
  longitude?: number;
  page?: number;
  radius?: number;
  size?: number;
};

/** @sync-ignore — frontend options bag for getTeams query/body params */
export type FetchTeamsOptions = {
  searchTerm?: string;
  teamClassIds?: number[];
  teamIds?: number[];
  teamKeys?: string[];
  page?: number;
  size?: number;
  sort?: string;
  signal?: AbortSignal;
};

/** @sync-ignore — request body shape; backend class path unknown, find via /sync-api-types once auth is set up */
export type ApplicationQuestionRequest = {
  comment?: string;
  field?: string;
  questionId: number;
  questionType: QuestionType;
  teamInfluencerClassQuestion?: boolean;
  userAccountConnectionId?: number;
  userAnswers?: number[];
};

/** @sync-ignore — request body shape; backend class path unknown, find via /sync-api-types once auth is set up */
export type ApplicationRequest = {
  applicationType: ApplicationType;
  locationId?: number;
  questions: ApplicationQuestionRequest[];
  referer?: string;
  referredOrgId?: number;
  teamApplicationFormId: number;
  teamCode?: string;
  teamId: number;
  userUuid: string;
  verificationEmail?: string;
};

// ── API ───────────────────────────────────────────────────────────────────────

export function getTeamCode(groupCode: string): Promise<TeamCodeResponse> {
  return get<TeamCodeResponse>(`/xapi/user-affiliations/pub/1.0/teamCodes/${groupCode}`);
}

export function postTeamCode(groupCode: string): Promise<TeamCodeSubmitResponse> {
  return post<TeamCodeSubmitResponse>(`/xapi/user-affiliations/ext/1.0/teamCodes/${groupCode}`);
}

export function postLocation(data: CreateLocationRequest): Promise<Location> {
  return post<Location>('/xapi/user-affiliations/ext/1.0/location', data);
}

export function getEmailRules(formId: number, email?: string): Promise<EmailRule> {
  return get<EmailRule>(`/xapi/user-affiliations/ext/1.0/applications/form/${formId}/emails`, {
    params: { email: email || undefined },
  });
}

export function getLocations(options: FetchLocationsOptions): Promise<PaginatedResponse<Location>> {
  const { teamId, latitude, longitude, page, radius, size } = options;
  return get<PaginatedResponse<Location>>(`/xapi/user-affiliations/ext/1.0/teams/${teamId}/locations`, {
    params: {
      lat: latitude || undefined,
      lon: longitude || undefined,
      page: page || undefined,
      radius: radius || undefined,
      size: size || undefined,
    },
  });
}

export function getOnboardingAffiliations(): Promise<OnboardingAffiliationsResponse> {
  return get<OnboardingAffiliationsResponse>('/xapi/user-affiliations/ext/1.0/onboarding/affiliations');
}

export function getPublicApplicationForm(
  teamId: number,
  options?: { latitude?: number; longitude?: number },
): Promise<ApplicationForm> {
  return get<ApplicationForm>(`/xapi/user-affiliations/ext/1.0/applications/teams/${teamId}/publicApplicationForm`, {
    params: { lat: options?.latitude || undefined, lon: options?.longitude || undefined },
  });
}

export function getRenewalApplicationForm(
  teamId: number,
  options?: { latitude?: number; longitude?: number },
): Promise<ApplicationForm> {
  return get<ApplicationForm>(`/xapi/user-affiliations/ext/1.0/applications/teams/${teamId}/renewalApplicationForm`, {
    params: { lat: options?.latitude || undefined, lon: options?.longitude || undefined },
  });
}

export function getTeam(params: { teamId?: number; teamKey?: string }): Promise<Team> {
  const { teamId, teamKey } = params;
  const path = teamId ? `${teamId}` : `teamKey/${teamKey}`;
  return get<Team>(`/xapi/user-affiliations/pub/1.0/teams/${path}`);
}

export function getTeams(options: FetchTeamsOptions = {}): Promise<PaginatedResponse<Team>> {
  const { page, size, sort, searchTerm, signal, teamClassIds, teamIds, teamKeys } = options;
  return post<PaginatedResponse<Team>>(
    '/xapi/user-affiliations/ext/1.0/teams',
    {
      includePrivate: teamIds?.length ? true : undefined,
      includePrivateToProfile: teamIds?.length ? true : undefined,
      name: searchTerm,
      teamClassIds,
      teamIds,
      teamKeys,
    },
    {
      params: {
        page: page || undefined,
        size: size || teamIds?.length || undefined,
        sort: sort || undefined,
      },
      signal,
    },
  );
}

export function postVerificationStatuses(ids: number[]): Promise<DocumentVerificationResult[]> {
  return post<DocumentVerificationResult[]>('/xapi/user-affiliations/ext/1.0/documentVerification/bulk', ids);
}

export function getApplication(teamApplicationId: number): Promise<TeamApplication> {
  return get<TeamApplication>(`/xapi/user-affiliations/ext/1.0/applications/${teamApplicationId}`);
}

export function postApplication(
  request: ApplicationRequest,
  attachments?: Record<number, File>,
): Promise<ApplicationProcessingSummary> {
  const hasAttachments = attachments && Object.keys(attachments).length > 0;

  if (hasAttachments) {
    const formData = new FormData();
    formData.append('applicationForm', new Blob([JSON.stringify(request)], { type: 'application/json' }));
    Object.entries(attachments).forEach(([questionId, file]) => {
      formData.append(`attachment[${questionId}]`, file);
    });
    return post<ApplicationProcessingSummary>('/xapi/user-affiliations/ext/1.0/applications', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  return post<ApplicationProcessingSummary>('/xapi/user-affiliations/ext/1.0/applications', request);
}

export function putUnsubmitApplication(teamApplicationId: number): Promise<void> {
  return put(`/xapi/user-affiliations/ext/1.0/applications/${teamApplicationId}/unsubmit`);
}

export function postApplicationVerificationEmail(teamApplicationId: number): Promise<void> {
  return post(`/xapi/user-affiliations/ext/1.0/applications/${teamApplicationId}/sendVerificationEmail`);
}

export function putLocation(affiliationId: number, locationId: number): Promise<void> {
  return put(`/xapi/user-affiliations/ext/1.0/affiliations/${affiliationId}`, null, {
    params: { location: locationId },
  });
}

export function getMyTeams(forceRefresh): Promise<MyTeamEntry[]> {
  const endpoint = `/xapi/user-affiliations/ext/1.0/relationship/myTeams${forceRefresh ? '?forceRefresh=true' : ''}`;
  return get<MyTeamEntry[]>(endpoint);
}
